/* =========================================================================
 * CHAMPIONSHIP RECORD INTEGRITY — the check that keeps a state fixed.
 *
 * Brian, after the Texas/UIL audit: "so this needs to be fully fixed and done
 * durable… this is one state. just imagine all the rest of the states."
 *
 * He is right that the Texas findings were not a Texas problem. Measured across
 * all 13,393 records, only THREE of 65 states were clean:
 *
 *     3,489  no championship key   — nothing can reliably dedupe or re-match them
 *     2,580  no event date         — invisible in Stages / Worklist / Calendar
 *       403  duplicates            — the same championship twice in one season
 *       123  no sport              — vanish from every sport filter
 *        61  state-level, no state — missing from the map and state filters
 *
 * ⚠️ WHAT THIS CAN AND CANNOT SEE, STATED UP FRONT.
 *
 * Four of the five Texas defects are STRUCTURAL — detectable from the records
 * alone, with no outside source: the missing keys, the missing dates and sports,
 * the duplicates, and the parent/child overlap that had Texas football's 6A
 * existing three times (a bare "6A" beside "6A DI" and "6A DII").
 *
 * The other two are NOT, and no amount of cleverness here will find them:
 *   · Swimming held 1A-6A when UIL crowns only "4A & Below, 5A, 6A" — six
 *     records for championships that do not exist
 *   · Military Marching Band was missing entirely
 * Both need the governing body's OFFICIAL list for that state, which is what
 * Dalton's UIL sheet is for Texas. So a state passing this audit is NOT the
 * same as a state being complete, and `completenessChecked` says so per state
 * rather than letting a green result imply something it has not verified.
 * Claiming otherwise would be the exact failure this file exists to catch.
 *
 * ⚠️ IT REPORTS. IT DOES NOT WRITE. Every defect here needs judgement — which
 * of two duplicates is the good one, whether a missing date is unknown or just
 * unscheduled. Auto-resolving them is how a bad record becomes the surviving
 * record. Pure, no imports; the test drives it directly.
 * ========================================================================= */

import { needsTitleType } from './conferenceTitles.js'
import { noDateIsExpected } from './decidedBy.js'

const S = (v) => String(v == null ? '' : v).trim()

/* =========================================================================
 * THE DEFECT CATALOG — what is wrong, per record, and what can be done.
 *
 * Brian, looking at the integrity page on his phone: "What am I supposed to do
 * with this. There isnt a call to action. It doesn't allow me to see the issues
 * or to review or change them."
 *
 * He is right, and the gap was in this file. It counted defects and returned
 * TOTALS — 288 with no key, 3,370 with no date — which tells you a problem
 * exists and nothing about which records have it. A number you cannot open is a
 * number you cannot act on, and a page of those is a report, not a tool.
 *
 * So each defect is declared here once: how to detect it, what it costs, and
 * whether a machine may fix it. `fixable` is the honest half — a missing DATE
 * cannot be invented, so its call to action is "work it", not "repair it", and
 * saying so is more useful than a button that would guess.
 * ========================================================================= */
/* ⚠️ WHO IS A DEFECT FOR. The catalog was one flat list, so a loader's
 * bookkeeping concern and "this championship has no sport and vanishes from
 * every filter" carried identical weight on screen. They are not the same
 * thing and should never have been added together. */
export const BLOCKING = 'blocking'   // stops a person working the record
export const HYGIENE = 'hygiene'     // internal bookkeeping; invisible to the board
/* Worth seeing, not a defect: a search gap, a retired record, a projected-only
 * date. Shown in the drawer as its own group and NEVER in the card's count. */
export const ADVISORY = 'advisory'

/* ⚠️ A PROJECTED DATE IS NOT A DATE. 1,030 of the 1,531 in-state undated
 * records carried sr_date_status "Projected" and 647 of them a real
 * sr_projected_date, and the old test (`!sr_event_date` alone) counted all of
 * them as "no date". They are counted apart now, and a projected date is still
 * never treated as the event date anywhere a date gates behaviour. */
const hasEventDate = (r) => Boolean(S(r.sr_event_date))
const hasProjectedDate = (r) => Boolean(S(r.sr_projected_date))

const NON_STATE_BUCKET = '(not state-scoped)'

/**
 * Which row of the by-state table a record belongs to.
 *
 * Extracted so the aggregate and the drill-in list CANNOT disagree. They were
 * two copies of the same rule for about ten minutes, which is exactly long
 * enough for a state to show 38 defects and its drill-in to show 41.
 */
export function bucketFor(record) {
  const r = record?.raw || record || {}
  const scoped = isStateScoped(r)
  return S(r.sr_state_location) || S(r.sr_state) || (scoped ? '(state missing)' : NON_STATE_BUCKET)
}

export const DEFECTS = [
  {
    /* ⚠️ DEMOTED TO HYGIENE, AND THIS IS THE BIGGEST CHANGE IN THE FILE.
     *
     * Brian: "the defects youve got shown dont really seem like a defect. i
     * dont really understand why they are being flagged a defect."
     *
     * He is right, and the honest answer is that this one is not a defect a
     * REP can act on. `sr_championship_key` is used in exactly one place —
     * idempotent bulk loading, so re-running a create does not duplicate a
     * record. It has NO bearing on the Command Center, the hunt, the artwork
     * chain, assignment, or anything a person does with a championship.
     *
     * And it was the loudest item on the page: ~3,489 of the records carry no
     * key, which is roughly half of every "defect" reported. An integrity page
     * whose biggest number is something nobody should care about teaches people
     * to ignore the page, and then the REAL defects — a championship with no
     * sport, invisible in every filter — go unworked behind it.
     *
     * So it stays checkable, and stops counting as a defect. */
    audience: HYGIENE,
    id: 'noKey',
    label: 'No championship key',
    /* The anchor every load matches on. Without it a record is invisible to the
     * next acquisition and can be silently duplicated. */
    cost: 'Invisible to every future load — it can be duplicated without anything noticing.',
    test: (r) => !S(r.sr_championship_key),
    fixable: 'backfill-key',
    fixLabel: 'Backfill the key',
  },
  {
    id: 'noDate',
    label: 'No date at all',
    cost: 'Falls into NO DATE — absent from Stages, Worklist and the Calendar, so nobody works it at the right time.',
    /* ⚠️ A MISSING DATE IS ONLY A DEFECT WHEN AN EVENT ACTUALLY EXISTS.
     *
     * Brian: "SOCon football is decided by standings only. They don't have a
     * championship that happens… if we are solely relying on a championship
     * date to trigger search, we won't find a champion."
     *
     * Most FCS and many D2 conferences stage NO football championship game —
     * the regular-season table decides it, co-champions and all. Those records
     * are CORRECT with no date, and flagging them was a false positive that
     * buried the genuinely missing ones: a CIF or NCHSAA state final really
     * does happen on a day, and its blank date is real work for a human.
     *
     * `decidedBy` separates the two, so this defect now means what it says. */
    test: (r) => !hasEventDate(r) && !hasProjectedDate(r) && !noDateIsExpected(r),
    /* ⚠️ DELIBERATELY NOT FIXABLE. A date cannot be derived from the record, and
     * a wrong date is worse than a blank one: it puts the championship in the
     * wrong countdown bucket and a rep works it at the wrong time. The action is
     * to find the real date, which is human work. */
    fixable: null,
    fixLabel: '',
  },
  {
    audience: ADVISORY,
    id: 'projectedOnly',
    label: 'Projected date only',
    cost: 'Has a projected window but no confirmed event date. Not a missing date, so it is not counted as a defect, and the projection is never used as the real date.',
    test: (r) => !hasEventDate(r) && hasProjectedDate(r) && !noDateIsExpected(r),
    fixable: null,
    fixLabel: '',
  },
  {
    /* ⚠️ NOT A DEFECT — A SEARCH GAP, and it is the one nothing else reports.
     *
     * A standings-decided title has no event, so a date-windowed sweep never
     * includes it, so nothing ever asks about it and nothing ever complains.
     * That is not a failed search, it is the ABSENCE of one, invisible by
     * construction. Surfacing it here is the only way it becomes work. */
    audience: ADVISORY,
    id: 'standingsNoHunt',
    label: 'Decided by standings — needs a season-end hunt',
    cost: 'No event date because no event exists. A date-triggered sweep can never reach it, so the champion is never looked for at all.',
    test: (r) => noDateIsExpected(r) && !S(r.current_champion),
    fixable: null,
    fixLabel: '',
  },
  {
    id: 'noSport',
    label: 'No sport',
    cost: 'Disappears from every sport filter.',
    test: (r) => !S(r.sr_sport),
    fixable: null,
    fixLabel: '',
  },
  {
    id: 'noState',
    label: 'State-level, no state',
    cost: 'Invisible to every state filter and to the map.',
    test: (r) => isStateScoped(r) && !S(r.sr_state_location) && !S(r.sr_state),
    /* Only the ones whose governing body names the state unambiguously; the
     * server-side rule decides, and it leaves the rest alone. */
    fixable: 'repair-defects:ca-missing-state',
    fixLabel: 'Set the state where it is unambiguous',
  },
  {
    audience: ADVISORY,
    id: 'retired',
    label: 'Retired',
    cost: 'Kept for the audit trail. Not a live championship — no champion will ever be crowned.',
    test: (r) => /^RETIRED - /.test(S(r.championship_name)),
    fixable: null,
    fixLabel: '',
  },
  {
    /**
     * ⚠️ THE CONFERENCE RECORD DOES NOT SAY WHICH OF THE TWO TITLES IT IS.
     *
     * Brian: "Yes, conference regular title and tournament title get recorded."
     *
     * A conference crowns a REGULAR-SEASON champion (won over the whole season
     * by standings) and, separately, a TOURNAMENT champion (won in three days
     * in March). They are different schools in most years — the ACC's 2025
     * baseball titles went to Georgia Tech and North Carolina respectively.
     *
     * Of 3,139 conference records on the object, TWELVE name which one they
     * are. So this is not just a gap in what we hold; the records we DO hold
     * are under-specified, and a rep looking at "Big East Women's Basketball
     * Championship" cannot tell which title it tracks — or whether the other
     * one is missing or is this record under a different reading.
     *
     * ⚠️ NO AUTOMATIC FIX, AND THAT IS DELIBERATE. Which title an existing
     * record represents cannot be derived from the record: it has to be read
     * off the conference's own site. Guessing "tournament" because that is the
     * common usage would silently relabel thousands of records, and where the
     * guess was wrong it would attach a real champion to the wrong title.
     *
     * ⚠️ MEET SPORTS AND FOOTBALL ARE NOT FLAGGED. Cross country, track,
     * swimming, golf, wrestling, gymnastics and rowing hold ONE championship
     * meet — "Big Ten Men's Cross Country Championship" is already complete.
     * Football crowns one champion via its title game. Flagging those 1,383
     * records would bury the ~1,700 that need a real decision under noise
     * nobody can act on, which is exactly how an integrity page becomes
     * wallpaper. See `needsTitleType` in lib/conferenceTitles.js.
     */
    audience: ADVISORY,
    id: 'confTitleAmbiguous',
    label: 'Conference title unspecified',
    cost: 'A conference crowns a regular-season AND a tournament champion — usually different schools. This record does not say which one it tracks, so its counterpart cannot be told apart from a duplicate.',
    test: (r) => needsTitleType(r),
    fixable: null,
    fixLabel: '',
  },
  /* SET-LEVEL: no per-record test. `auditSet` assigns these, because a record
   * cannot be a duplicate on its own. Declared here so they have a label, a
   * cost and an audience like every other defect. */
  {
    id: 'duplicate',
    label: 'Possible duplicate',
    cost: 'Another record in this set has the same name, season, gender and sport. Which one survives is a human call.',
    test: null,
    fixable: null,
    fixLabel: '',
  },
  {
    id: 'overlap',
    label: 'Class beside its own divisions',
    cost: 'A bare class exists alongside its DI / DII split, so this record can never have a single winner.',
    test: null,
    fixable: null,
    fixLabel: '',
  },
]

/**
 * The backend rule id a `fixable` value names, or null when it names none.
 *
 * ⚠️ THE ONE PLACE THIS IS PARSED, because two drawers parsed it two ways. The
 * Integrity drawer split `repair-defects:ca-missing-state` to `ca-missing-state`;
 * the Governing Bodies drawer sent the whole literal. The backend matched no
 * rule and no-opped, so its "0 would change" was a silent lie rather than a
 * write.
 *
 * ⚠️ AND NULL MUST NEVER REACH THE ROUTE. `repairDefects({ only })` in
 * sr-portal-backend runs EVERY rule when `only` is falsy, including
 * `sourceless-crown`, which blanks champions. A caller that gets null here must
 * refuse to call, never pass it through as "no filter".
 */
export function repairRuleFor(fixable) {
  const m = /^repair-defects:([a-z0-9-]+)$/.exec(S(fixable))
  return m ? m[1] : null
}

/**
 * Every defect on ONE record, as ids. The page renders chips from these, so a
 * record says what is wrong with it rather than merely being counted somewhere.
 */
export function defectsFor(record) {
  const r = record?.raw || record || {}
  return DEFECTS.filter((d) => d.test && d.test(r)).map((d) => d.id)
}

/**
 * The defective records themselves — the thing the page could not show.
 *
 * Capped, and the cap is REPORTED rather than silently applied: 3,370 rows of
 * anything is not reviewable, and a list that quietly stops at 500 is a list
 * that lies about the size of the problem.
 */
export function defectiveRecords(records = [], { defect = null, state = null, limit = 500 } = {}) {
  const out = []
  let matched = 0
  for (const rec of records) {
    const r = rec?.raw || rec || {}
    const ids = defectsFor(r)
    if (!ids.length) continue
    if (defect && !ids.includes(defect)) continue
    if (state && bucketFor(r) !== state) continue
    matched += 1
    if (out.length < limit) {
      out.push({
        id: S(r.id) || S(rec?.id),
        name: S(r.championship_name) || S(rec?.name) || 'Untitled',
        state: bucketFor(r),
        sport: S(r.sr_sport),
        gender: S(r.sr_gender),
        season: S(r.sr_season_year),
        defects: ids,
      })
    }
  }
  return { rows: out, matched, capped: matched > out.length, limit }
}

/* A record that is not state-scoped is not missing a state. Conference,
 * national, college, professional and world championships legitimately have
 * none — 3,563 records, and treating them as broken would have sent somebody
 * chasing a number that is correct by design. Only a STATE-level record with no
 * state is a defect. */
const STATE_SCOPED = /state/i

export function isStateScoped(r) {
  return STATE_SCOPED.test(S(r.sr_level)) || STATE_SCOPED.test(S(r.sr_segment))
}

/* Normalise a championship name so the same event written two ways collides.
 * Strips the governing body, the state, and the words every record shares, and
 * folds "Division I" onto "DI" — which is how Texas football's twelve 2025
 * records and its twelve 2026 records turned out to be the same twelve events
 * under two naming schemes. */
/* The state's own name is noise INSIDE a state bucket — "Texas UIL 6A Football"
 * and "UIL (public) Football 6A" are one championship, and leaving the word in
 * kept them apart. Stripped generally rather than per-state, because every state
 * writes its own name into some of its records and none of the others. */
const STATE_WORDS = new RegExp(`\\b(${[
  'alabama', 'alaska', 'arizona', 'arkansas', 'california', 'colorado', 'connecticut', 'delaware',
  'district of columbia', 'florida', 'georgia', 'hawaii', 'idaho', 'illinois', 'indiana', 'iowa',
  'kansas', 'kentucky', 'louisiana', 'maine', 'maryland', 'massachusetts', 'michigan', 'minnesota',
  'mississippi', 'missouri', 'montana', 'nebraska', 'nevada', 'new hampshire', 'new jersey',
  'new mexico', 'new york', 'north carolina', 'north dakota', 'ohio', 'oklahoma', 'oregon',
  'pennsylvania', 'rhode island', 'south carolina', 'south dakota', 'tennessee', 'texas', 'utah',
  'vermont', 'virginia', 'washington', 'west virginia', 'wisconsin', 'wyoming',
].join('|')})\\b`, 'g')

export function normalizeName(name) {
  return S(name).toLowerCase()
    /* ⚠️ (boys) and (girls) are NOT stripped any more. Stripping them made a
     * boys' and a girls' title of one event hash identically: 26 of the
     * flagged "duplicates" were exactly those pairs. */
    .replace(/\((?:public|private\/independent|six-man)\)/g, ' ')
    .replace(STATE_WORDS, ' ')
    .replace(/\b(uil|tapps|state|championship|championships|public|private|independent)\b/g, ' ')
    .replace(/\bdivision\s+ii\b/g, ' dii')
    .replace(/\bdivision\s+i\b/g, ' di')
    .replace(/[^a-z0-9 ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/* ⚠️ THE OVERLAP CHECK — the one that found Texas football.
 * A bare class sitting alongside its own divisions means the championship is
 * recorded twice at different granularities: "Football Boys 6A" is not a real
 * championship when "6A DI" and "6A DII" both exist. A rep working the bare one
 * is working a record that can never have a single winner. */
export function findOverlaps(names) {
  const set = new Set(names)
  const out = []
  for (const n of set) {
    const kids = [`${n} di`, `${n} dii`, `${n} division i`, `${n} division ii`]
      .filter((k) => set.has(k))
    if (kids.length) out.push({ parent: n, children: kids })
  }
  return out
}

const NO_DATE = DEFECTS.find((d) => d.id === 'noDate')
const PROJECTED_ONLY = DEFECTS.find((d) => d.id === 'projectedOnly')

/* =========================================================================
 * THE IDENTITY KEY — what makes two records "the same championship".
 *
 * ⚠️ IT WAS HASHING TWO FIELDS THAT ARE NOT THERE. The key was
 * [name | sr_season_year | sr_gender | sport]. sr-portal-backend's CORE_PROPS
 * does request both, but they come back EMPTY on every record in the live
 * payload, so both slots were '' everywhere and the key was really name|sport.
 * With (boys)/(girls) also stripped from the name, a boys' and a girls' title
 * of one event hashed identically.
 *
 * Fixed here rather than in the payload because the payload already asks for
 * both: the gap is the DATA, and the backend is not modified from this repo
 * anyway. So each slot is filled from what the record actually carries:
 *   · gender: sr_gender if set, else the gender word in the name
 *   · season: sr_season_year if set, else the year of the record's own date
 *     (event, then projected). The projected year is used only as IDENTITY
 *     here, never as a date that gates anything.
 * A slot nothing answers stays blank, which can only merge LESS than before.
 * ========================================================================= */
const yearOf = (v) => {
  const s = S(v)
  if (!s) return ''
  const iso = /^(\d{4})-/.exec(s)
  if (iso) return iso[1]
  if (/^\d{10,}$/.test(s)) { const d = new Date(Number(s)); return Number.isNaN(d.getTime()) ? '' : String(d.getUTCFullYear()) }
  return ''
}

export function genderOf(record) {
  const r = record?.raw || record || {}
  const g = S(r.sr_gender).toLowerCase()
  if (g) {
    if (/^(w|g|f|lady|ladies)/.test(g)) return 'w'
    if (/^(m|b)/.test(g)) return 'm'
    return g
  }
  const n = S(r.championship_name ?? record?.name).toLowerCase()
  if (/\b(girls|women|womens|women's|lady|ladies)\b/.test(n)) return 'w'
  if (/\b(boys|men|mens|men's)\b/.test(n)) return 'm'
  return ''
}

export function seasonOf(record) {
  const r = record?.raw || record || {}
  return S(r.sr_season_year) || yearOf(r.sr_event_date) || yearOf(r.sr_projected_date)
}

export function identityKey(record) {
  const r = record?.raw || record || {}
  return [
    normalizeName(r.championship_name),
    seasonOf(r),
    genderOf(r),
    S(r.sr_sport).toLowerCase(),
  ].join('|')
}

/**
 * EVERY DEFECT ON EVERY RECORD OF ONE SET, including the two that need the
 * whole set to see (duplicate, overlap). This is what a Governing Bodies card
 * counts AND what its drawer lists.
 *
 * ⚠️ WHY THIS EXISTS. The card's defect badge used to read the STATE audit
 * (`byState.get(entity.state)`) while every other number on the card read the
 * BODY's own records. So AHSAA's badge said 13 while its drawer said 0 (the 13
 * belonged to AISA, NASP, NHSRA and SCTP), California's 130 printed on all
 * eleven CIF cards, and College / Canada / Pro, whose names are not states,
 * showed nothing at all. One function, one set of rows, one number.
 *
 * `blocking` counts RECORDS with at least one blocking defect: a record with no
 * date and no sport is one piece of work, not two.
 */
export function auditSet(records = []) {
  const items = []
  const byKey = new Map()
  for (const rec of records) {
    const r = rec?.raw || rec || {}
    const ids = defectsFor(r)
    const key = identityKey(r)
    const it = { rec, raw: r, ids, key }
    items.push(it)
    if (!byKey.has(key)) byKey.set(key, [])
    byKey.get(key).push(it)
  }
  for (const group of byKey.values()) {
    if (group.length > 1) for (const it of group) it.ids.push('duplicate')
  }
  const names = new Map()
  for (const it of items) {
    const n = it.key.split('|')[0]
    if (!names.has(n)) names.set(n, [])
    names.get(n).push(it)
  }
  for (const o of findOverlaps([...names.keys()])) {
    for (const it of names.get(o.parent) || []) it.ids.push('overlap')
  }
  const audienceOf = (id) => DEFECTS.find((d) => d.id === id)?.audience || BLOCKING
  let blocking = 0
  const counts = new Map()
  for (const it of items) {
    for (const id of it.ids) counts.set(id, (counts.get(id) || 0) + 1)
    it.blocking = it.ids.some((id) => audienceOf(id) === BLOCKING)
    if (it.blocking) blocking += 1
  }
  return { items, counts, blocking, total: items.length }
}

const EMPTY = () => ({
  records: 0, noKey: 0, noDate: 0, noSport: 0, noLevel: 0, noStateOnStateRecord: 0,
  duplicateGroups: 0, duplicateExtra: 0, overlaps: 0,
})

/**
 * auditChampionships(records) → { states, totals, generatedAt }
 *
 * One row per state that holds records, plus a NOT-STATE-SCOPED row so the
 * conference/national/college block is visible rather than silently excluded.
 */
export function auditChampionships(records = []) {
  const buckets = new Map()
  const NON_STATE = '(not state-scoped)'

  for (const raw of records) {
    /* ⚠️ THE AUDIT READS RAW HUBSPOT PROPERTIES, AND ITS CALLER FEEDS IT
     * NORMALIZED ONES. `useChampionships` runs every record through
     * `normalizeChampionship`, which returns a NEW object of camelCase fields and
     * parks the original under `.raw`. So every `r.sr_*` read here resolved to
     * undefined and the page reported all 13,393 records as having no key, no
     * date and no sport, and 13,392 of them as duplicates — because
     * `normalizeName(undefined)` is the SAME empty string for every record, so
     * they all collided into one name bucket.
     *
     * A scan claiming 100% of the data is broken is almost always the scan being
     * broken: the Command Center reads those same fields fine, off the same
     * feed. Unwrapping here rather than at the call site keeps the function
     * correct for BOTH shapes, so it cannot silently invert again the next time
     * a caller passes it the other one. */
    const r = raw?.raw || raw
    const scoped = isStateScoped(r)
    const st = bucketFor(r)
    if (!buckets.has(st)) buckets.set(st, { ...EMPTY(), state: st, names: new Map(), samples: [] })
    const b = buckets.get(st)

    b.records += 1
    if (!S(r.sr_championship_key)) b.noKey += 1
    /* The SAME test the catalog uses, so this table and its drill-in agree. */
    if (NO_DATE.test(r)) b.noDate += 1
    if (PROJECTED_ONLY.test(r)) b.projectedOnly = (b.projectedOnly || 0) + 1
    if (!S(r.sr_sport)) b.noSport += 1
    if (!S(r.sr_level)) b.noLevel += 1
    if (scoped && !S(r.sr_state_location) && !S(r.sr_state)) b.noStateOnStateRecord += 1

    /* ⚠️ GENDER AND SPORT ARE PART OF THE IDENTITY, and leaving them out is why
     * this reported 353 duplicates that do not exist.
     *
     * NJCAA, NAIA and NCCAA crown a MEN'S and a WOMEN'S championship under the
     * same short name — "NJCAA Outdoor Track" is two titles, two winners, two
     * ring orders. NCCAA regional names collide across sports the same way:
     * "NCCAA DI North Central Region" is both a softball and a cross country
     * championship. Keyed on name alone, every one of those pairs read as a
     * duplicate of itself.
     *
     * Measured against all 14,183 records: of 117 flagged groups, ZERO had a
     * copy that was genuinely redundant. Every single one was distinguished by
     * a field this key was not looking at. The defect was never duplication —
     * it is that these names are UNDER-SPECIFIED, and the fix is to name them,
     * not to merge them.
     *
     * The season stays in the key because the same championship in two seasons
     * is two records by design. */
    const key = identityKey(r)
    if (!b.names.has(key)) b.names.set(key, [])
    b.names.get(key).push(S(r.id))
  }

  const states = []
  for (const b of buckets.values()) {
    const dupes = []
    for (const [key, ids] of b.names) {
      if (ids.length > 1) {
        b.duplicateGroups += 1
        b.duplicateExtra += ids.length - 1
        if (dupes.length < 25) dupes.push({ name: key.split('|')[0], season: key.split('|')[1], ids })
      }
    }
    const overlaps = findOverlaps([...b.names.keys()].map((k) => k.split('|')[0]))
    b.overlaps = overlaps.length

    const { names: _names, samples: _samples, ...rest } = b
    states.push({
      ...rest,
      /* ⚠️ `issues` IS THE NUMBER ON THE CARD, so it counts only what blocks
       * somebody. noKey is tracked and reported separately rather than summed
       * in — see the HYGIENE note on that entry. */
      issues: b.noDate + b.noSport + b.noStateOnStateRecord + b.duplicateExtra + b.overlaps,
      hygiene: b.noKey,
      clean: !(b.noDate || b.noSport || b.noStateOnStateRecord || b.duplicateExtra || b.overlaps),
      /* ⚠️ Never implied. A state can pass every structural check and still be
       * missing whole championships — only its governing body's official list
       * can answer that, and we hold one for Texas alone. */
      completenessChecked: false,
      duplicateSample: dupes,
      overlapSample: overlaps.slice(0, 15),
    })
  }

  states.sort((a, b) => b.issues - a.issues || a.state.localeCompare(b.state))

  const totals = states.reduce((a, s) => {
    for (const k of ['records', 'noKey', 'noDate', 'noSport', 'noLevel', 'noStateOnStateRecord', 'duplicateGroups', 'duplicateExtra', 'overlaps']) {
      a[k] = (a[k] || 0) + s[k]
    }
    return a
  }, {})
  totals.states = states.length
  totals.statesClean = states.filter((s) => s.clean).length
  totals.statesWithIssues = states.filter((s) => !s.clean).length

  return { generatedAt: Date.now(), states, totals }
}
